import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, Users, Briefcase, Calendar, 
  Megaphone, ChevronDown, School, GraduationCap,
  BarChart, ClipboardList, FileSpreadsheet, CalendarCheck,
  MessageCircle, Trophy, ClipboardCheck, Network,
  UserCheck, Presentation, LineChart, Building,Newspaper as NewsPaper
} from 'lucide-react';

const SidebarItem = ({ item, isActive, currentPath, closeMenu, activeGroup, setActiveGroup }) => {
  const hasSubItems = item.subItems?.length > 0;
  const isExpanded = activeGroup === item.title;

  return (
    <div className="relative group">
      <Link
        to={item.path || '#'}
        onClick={(e) => {
          if (hasSubItems) {
            e.preventDefault();
            setActiveGroup(isExpanded ? null : item.title);
          } else {
            closeMenu();
          }
        }}
        className={`w-full flex items-center justify-between p-2.5 rounded-xl
          transition-all duration-200 hover:bg-gradient-to-r hover:from-indigo-50/80 hover:to-indigo-50/40
          ${isActive 
            ? 'bg-gradient-to-r from-indigo-500/15 to-indigo-500/5 text-indigo-700 shadow-sm' 
            : 'text-gray-700 hover:text-indigo-600'}`}
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className={`p-1.5 rounded-lg transition-all duration-200
            ${isActive 
              ? 'bg-indigo-100 text-indigo-700 shadow-sm ring-1 ring-indigo-100' 
              : 'bg-gray-50 text-gray-600 group-hover:bg-indigo-50 group-hover:text-indigo-600'}`}>
            <item.icon className="w-4 h-4 shrink-0" strokeWidth={1.8} />
          </div>
          <span className={`font-medium text-[13px] tracking-wide truncate
            ${isActive ? 'text-indigo-700' : 'text-gray-700'}`}>
            {item.title}
          </span>
        </div>
        {hasSubItems && (
          <ChevronDown 
            className={`w-3.5 h-3.5 transition-all duration-300 text-gray-400
              ${isExpanded ? 'rotate-180 text-indigo-500' : ''}`} 
            strokeWidth={1.8}
          />
        )}
      </Link>

      {hasSubItems && (
        <div className={`overflow-hidden transition-all duration-300 pl-4
          ${isExpanded ? 'max-h-64 opacity-100 mt-1' : 'max-h-0 opacity-0'}`}>
          {item.subItems.map((subItem, index) => (
            <Link
              key={index}
              to={subItem.path}
              onClick={closeMenu}
              className={`flex items-center gap-2.5 p-2.5 rounded-xl mt-0.5 
                transition-all text-[13px] min-w-0 group/sub
                ${currentPath === subItem.path
                  ? 'bg-indigo-50/80 text-indigo-700 shadow-sm'
                  : 'text-gray-600 hover:bg-gray-50/80 hover:text-indigo-600'}`}
            >
              <div className={`p-1.5 rounded-lg transition-all duration-200
                ${currentPath === subItem.path 
                  ? 'bg-indigo-100 text-indigo-700 shadow-sm' 
                  : 'bg-gray-50/80 text-gray-500 group-hover/sub:bg-gray-100 group-hover/sub:text-indigo-600'}`}>
                <subItem.icon className="w-3.5 h-3.5 shrink-0" strokeWidth={1.8} />
              </div>
              <span className="font-medium truncate tracking-wide">{subItem.title}</span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

const Sidebar = ({ isOpen, setIsOpen, collegeInfo }) => {
  const location = useLocation();
  const [activeGroup, setActiveGroup] = React.useState(null);

  const menuItems = [
    {
      title: 'Dashboard',
      icon: LayoutDashboard,
      path: '/college/dashboard'
    },
    {
      title: 'Alumni',
      icon: Users,
      subItems: [
        { title: 'Directory', icon: UserCheck, path: '/college/alumni' },
        { title: 'Analytics', icon: LineChart, path: '/college/alumni-analytics' }
      ]
    },
    {
      title: 'Jobs',
      icon: Briefcase,
      subItems: [
        { title: 'Listings', icon: ClipboardList, path: '/college/jobs' },
        { title: 'Statistics', icon: BarChart, path: '/college/job-analytics' }
      ]
    },
    {
      title: 'Events',
      icon: Calendar,
      subItems: [
        { title: 'Calendar', icon: CalendarCheck, path: '/college/events' },
        { title: 'Reports', icon: FileSpreadsheet, path: '/college/events-analytics' }
      ]
    },
    {
      title: 'Campaigns',
      icon: Megaphone,
      subItems: [
        { title: 'Active', icon: Network, path: '/college/campaigns' },
        { title: 'Reports', icon: Presentation, path: '/college/campaigns-analytics' }
      ]
    },
    {
      title: 'Success Stories',
      icon: Trophy,
      subItems: [
        { title: 'Stories', icon: MessageCircle, path: '/college/successstorylist' },
      ]
    },
    {
      title: 'Surveys',
      icon: ClipboardCheck,
      subItems: [
        { title: 'Active', icon: ClipboardList, path: '/college/survey' },
     
      ]
    },
    {
      title: 'NewsLetters',
      icon: NewsPaper,
      subItems: [
        { title: 'List', icon: ClipboardList, path: '/college/newsletters' },
     
      ]
    },
   
    {
      title: 'Feedbacks',
      icon: Megaphone,
      subItems: [
        { title: 'List', icon: MessageCircle, path: '/college/feedback' },
       
      ]
    }
  ];

  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (window.innerWidth < 1024) {
        const sidebar = document.getElementById('sidebar');
        if (isOpen && sidebar && !sidebar.contains(event.target)) {
          setIsOpen(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, setIsOpen]);

  React.useEffect(() => {
    if (window.innerWidth < 1024) {
      setIsOpen(false);
    }
  }, [location.pathname]);

  const closeMenu = () => {
    if (window.innerWidth < 1024) {
      setIsOpen(false);
    }
  };

  return (
    <>
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}

      <aside
        id="sidebar"
        className={`fixed top-0 left-0 z-40 h-screen bg-white/95 backdrop-blur-md
          shadow-lg shadow-gray-200/50 transition-all duration-300 ease-in-out
          border-r border-gray-100/80 w-64
          ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}
      >
        <div className="h-16 px-4 flex items-center justify-between border-b border-gray-100/80">
          <div className="flex items-center gap-3 min-w-0">
            {collegeInfo?.logo ? (
              <img 
                src={collegeInfo.logo}
                alt={collegeInfo.name} 
                className="w-8 h-8 rounded-lg object-contain ring-1 ring-gray-100"
              />
            ) : (
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-50 to-indigo-100 
                flex items-center justify-center ring-1 ring-indigo-100">
                <Building className="w-4 h-4 text-indigo-600" strokeWidth={1.8} />
              </div>
            )}
            <div className="min-w-0">
              <h2 className="text-sm font-semibold text-gray-900 tracking-wide truncate">
                {collegeInfo?.name || 'College Name'}
              </h2>
              <p className="text-xs text-gray-500 truncate">
                {collegeInfo?.location || 'Location'}
              </p>
            </div>
          </div>
        </div>

        <div className="p-3 space-y-1 overflow-y-auto h-[calc(100vh-4rem)]">
          {menuItems.map((item, index) => (
            <SidebarItem
              key={index}
              item={item}
              isActive={
                item.path 
                  ? location.pathname === item.path
                  : item.subItems?.some(sub => location.pathname === sub.path)
              }
              currentPath={location.pathname}
              closeMenu={closeMenu}
              activeGroup={activeGroup}
              setActiveGroup={setActiveGroup}
            />
          ))}
        </div>
      </aside>
    </>
  );
};

export default Sidebar;